import type * as React from "react"

import { cn } from "@/utils/styles.helpers"

export interface DetailPanelProps
	extends Omit<React.ComponentProps<"section">, "title"> {
	/** The block's name, in the header bar. */
	title: React.ReactNode
	/** An icon before the title. */
	icon?: React.ComponentType<{ className?: string }>
	/** Tags or short facts right after the title, such as a status tag. */
	badges?: React.ReactNode
	/** Muted facts on the right of the header bar, such as dates. */
	meta?: React.ReactNode
	/** Controls on the right end of the header bar. */
	actions?: React.ReactNode
	/** Drops the body padding, for a table that runs edge to edge. */
	flush?: boolean
	/**
	 * Lets the actions drop under the title when the header is too narrow for
	 * both, for wide actions such as a segmented control and a link.
	 */
	wrapActions?: boolean
}

/**
 * One major block of a dense detail page (a dispute, the items, the
 * shipping): a bordered surface with a header bar (title, badges, meta,
 * actions) and a body. Sub-groups inside it are `DetailPanelSection`s,
 * separated by hairlines. Detail panels never nest.
 */
export function DetailPanel({
	title,
	icon: Icon,
	badges,
	meta,
	actions,
	flush = false,
	wrapActions = false,
	className,
	children,
	...props
}: DetailPanelProps) {
	return (
		<section
			data-slot="detail-panel"
			className={cn(
				"min-w-0 overflow-hidden rounded-lg border bg-card text-card-foreground",
				className,
			)}
			{...props}
		>
			<header
				className={cn(
					"flex min-h-11 items-start gap-3 border-b px-4 py-2.5",
					wrapActions && "flex-wrap gap-y-2",
				)}
			>
				<div
					className={cn(
						"flex min-w-0 flex-col gap-0.5",
						wrapActions ? "flex-[1_1_10rem]" : "flex-1",
					)}
				>
					<div className="flex min-h-6 min-w-0 flex-wrap items-center gap-2">
						{Icon && <Icon className="size-4 shrink-0 text-meta" />}
						<h2 className="text-sm font-semibold">{title}</h2>
						{badges}
					</div>
					{meta && <div className="text-xs text-meta tabular-nums">{meta}</div>}
				</div>
				{actions && (
					<div className="flex min-h-6 shrink-0 flex-wrap items-center justify-end gap-2">
						{actions}
					</div>
				)}
			</header>
			<div className={cn("flex min-w-0 flex-col", !flush && "divide-y")}>
				{children}
			</div>
		</section>
	)
}

export interface DetailPanelSectionProps
	extends Omit<React.ComponentProps<"div">, "title"> {
	/** A small label naming the sub-group; leave it out for the first, unnamed group. */
	label?: React.ReactNode
	/** A control on the right of the label. */
	action?: React.ReactNode
}

/**
 * A sub-group of a `DetailPanel`: 16px padding, an optional 11px label with an
 * action on its right, then the content. Siblings are split by a hairline.
 */
export function DetailPanelSection({
	label,
	action,
	className,
	children,
	...props
}: DetailPanelSectionProps) {
	return (
		<div
			data-slot="detail-panel-section"
			className={cn("flex min-w-0 flex-col gap-3 p-4", className)}
			{...props}
		>
			{(label || action) && (
				<div className="flex min-h-5 items-center justify-between gap-2">
					{label && (
						<h3 className="text-[11px] leading-4 font-semibold tracking-[0.07em] text-meta uppercase">
							{label}
						</h3>
					)}
					{action}
				</div>
			)}
			{children}
		</div>
	)
}
